import * as React from 'react'
import styled from 'styled-components'
import { PmType } from '../../../common/src/types.gen'

export enum PmItemType {
  FIRM = 'FIRM',
  PROPERTY = 'PROPERTY',
  UNIT = 'UNIT',
  FLOOR_PLAN = 'FLOOR_PLAN',
  PROSPECT = 'PROSPECT',
  APPLICATION = 'APPLICATION',
  APPOINTMENT = 'APPOINTMENT',
  SHOWING = 'SHOWING',
  CAMPAIGN = 'CAMPAIGN',
}

export interface PmItemKey {
  pmType: PmType
  firm: string
  property?: string
  floorPlan?: string
  unit?: string
  application?: string
  prospect?: string
  appointment?: string
  showing?: string
  campaign?: string
}

/**
 * Returns a URL
 *
 * May return something like: - https://foo.appfolio.com/properties/1
 *
 * For now, FIRM, PROPERTY, and UNIT AuditKeys always have a URL.
 */
export function getExternalPmUrl(pmItemKeyJson: string) {
  const pmItemKey = JSON.parse(pmItemKeyJson)
  const firm = pmItemKey.firm
  const pmItemType = getType(pmItemKey)
  const patterns: Map<PmType, string> = new Map<PmType, string>()

  switch (pmItemType) {
    case PmItemType.FIRM:
      patterns.set(PmType.APPFOLIO, 'https://' + firm + '.appfolio.com')
      break
    case PmItemType.PROPERTY:
      patterns.set(PmType.APPFOLIO, 'https://' + firm + '.appfolio.com/properties/' + pmItemKey.property)
      break
    case PmItemType.APPLICATION:
      patterns.set(PmType.APPFOLIO, 'https://' + firm + '.appfolio.com/rental_applications/' + pmItemKey.application)
      break
    case PmItemType.UNIT:
      patterns.set(
        PmType.APPFOLIO,
        'https://' + firm + '.appfolio.com/properties/' + pmItemKey.property + '/units/' + pmItemKey.unit
      )
      break
    case PmItemType.PROSPECT:
      patterns.set(PmType.APPFOLIO, 'https://' + firm + '.appfolio.com/guest_cards/' + pmItemKey.prospect)
      break
    default:
      break
  }

  return patterns.get(pmItemKey.pmType)?.toLowerCase()
}

function getType(pmItemKey: PmItemKey): PmItemType {
  if (isNotEmpty(pmItemKey.application)) {
    return PmItemType.APPLICATION
  } else if (isNotEmpty(pmItemKey.appointment)) {
    return PmItemType.APPOINTMENT
  } else if (isNotEmpty(pmItemKey.showing)) {
    return PmItemType.SHOWING
  } else if (isNotEmpty(pmItemKey.prospect)) {
    return PmItemType.PROSPECT
  } else if (isNotEmpty(pmItemKey.campaign)) {
    return PmItemType.CAMPAIGN
  } else if (isNotEmpty(pmItemKey.unit)) {
    return PmItemType.UNIT
  } else if (isNotEmpty(pmItemKey.floorPlan)) {
    return PmItemType.FLOOR_PLAN
  } else if (isNotEmpty(pmItemKey.property)) {
    return PmItemType.PROPERTY
  }
  return PmItemType.FIRM
}

/**
 * Returns true if str is not an empty string
 */
function isNotEmpty(str: string | undefined): boolean {
  if (str === undefined || str == null) {
    return false
  }
  return str !== ''
}

/**
 * Turns plain text into HTML.
 *
 * Creates clickable links for all URLs and turns newlines to HTML breaks.
 */
export function bodyToEscapedHtml(str: string) {
  const pattern = /(?:\r\n|\r|\n)/g
  let match = pattern.exec(str)
  if (match == null) {
    return makeClickableLink(str)
  }

  const elems = []
  let lastIndex = 0
  while (match != null) {
    const substr = str.substring(lastIndex, match.index)
    lastIndex = pattern.lastIndex
    elems.push(substr)
    elems.push(<br />)
    match = pattern.exec(str)
  }
  elems.push(str.substring(lastIndex))

  const res = []
  for (const e of elems) {
    if (typeof e === 'string') {
      res.push(makeClickableLink(e))
      continue
    }
    res.push(e)
  }

  return res
}

// urlRegexStr is a modified version of re in RichText::transform()
// which matches with URLs that start with 'www.' and email addresses in addition to URLs that start with 'http://'
// eslint-disable-next-line prettier/prettier
const urlRegexStr =
  '(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_+.~#?&\\/\\/=,!]*)\\b'
// email regex is from EmailToken.java
const emailRegexStr = "([a-zA-Z0-9.!#$%&'*+\\/=?^_`{|}~-]+)@([a-zA-Z0-9-]+(?:\\S[a-zA-Z0-9-]+)+)"
export const UrlAndEmailPattern = new RegExp('(?<email>' + emailRegexStr + ')|(?<url>' + urlRegexStr + ')', 'g')

function makeClickableLink(str: string) {
  let match = UrlAndEmailPattern.exec(str)
  if (match == null) {
    return str
  }

  const elems = []
  let lastIndex = 0
  while (match != null) {
    const substr = str.substring(lastIndex, match.index)
    lastIndex = UrlAndEmailPattern.lastIndex
    elems.push(substr)

    let link = match[0]
    const linkText = shortenUrl(link)

    // for email address links, include 'mailto:' in the beginning
    if (match.groups?.email !== undefined) {
      link = 'mailto:' + link
    }
    // make sure URLs not starting with http are not treated as relative paths
    if (match.groups?.url !== undefined && !link.startsWith('http')) {
      link = 'http://' + link
    }

    elems.push(
      <A key={elems.length} href={link}>
        {linkText}
      </A>
    )
    match = UrlAndEmailPattern.exec(str)
  }
  elems.push(str.substring(lastIndex))

  return elems
}

function shortenUrl(url: string) {
  return url.length < 30 ? url : url.substring(0, 27) + '…'
}

const A = styled.a({
  color: '#007e9e',
  transition: 'all 0.1s ease-in-out',
  $nest: {
    '&:hover': {
      color: '#07b5e3',
    },
  },
})
